// imported by /reducers/index.js
// holds road search parameters and result

import type { PrerenderUser, PrerenderUserResponse } from '../features/PrerenderUser';
import { defaultUser, PrerenderUserAction as ActionType } from '../features/PrerenderUser';
import { stringToDate } from '../assets/dateFormatter';

type UserSetAction = { type: ActionType.UserSet; payload: PrerenderUserResponse };
type UserTokenSetAction = { type: ActionType.UserTokenSet; payload: string };
type UserLoadingSetAction = { type: ActionType.UserLoadingSet; payload: { value: boolean } };
type UserAnauthorizedAction = { type: ActionType.UserAnauthorized };
type UserTrackingCodeSetAction = { type: ActionType.UserTrackingCodeSet; payload: boolean };
type UserShowOnboardingSetAction = { type: ActionType.UserShowOnboardingSet; payload: boolean };
type UserResetAction = { type: ActionType.UserReset };
type UserUnreadNotificationsDecreaseAction = { type: ActionType.UserUnreadNotificationsDecrease; payload: number };
type UserUnreadNotificationsIncreaseAction = { type: ActionType.UserUnreadNotificationsIncrease; payload: number };
type UserPatchAction = { type: ActionType.UserPatch; payload: Partial<PrerenderUser> };

type ReducerAction =
  | UserSetAction
  | UserTokenSetAction
  | UserLoadingSetAction
  | UserAnauthorizedAction
  | UserTrackingCodeSetAction
  | UserShowOnboardingSetAction
  | UserResetAction
  | UserUnreadNotificationsDecreaseAction
  | UserUnreadNotificationsIncreaseAction
  | UserPatchAction;

export default function prerenderUserReducer(state: PrerenderUser = defaultUser, action: ReducerAction): PrerenderUser {
  switch (action.type) {
    case ActionType.UserSet: {
      const {
        id,
        email,
        numPagesCached,
        token,
        signupDate,
        companyCreationDate,
        trackingCodeInstalled,
        cacheFreshness,
        hasBillingInfo,
        recachingDisabled,
        planName,
        costPerMonth,
        emailVerified,
        customPrice,
        customCachedPagesLimit,
        recacheDelay,
        userPausedRecaching,
        queryParamsWhitelist,
        ignoreAllQueryParams,
        hasBillingAddress,
        firstName,
        lastName,
        isMeteredBilling,
        plan,
        renderCounter,
        cancelledAt,
        hasCustomContractPricing,
        mobileAdaptive,
        settings,
        features,
        allURLCount,
        companyId,
        companyName,
        adminEmail,
        isTrial,
        isCacheFreshnessDisabled,
        nextBillingAt,
        lastCharged,
        customRecacheDelay,
        chargeBeePlanId,
        showRegistrationSteps,
        trackingCodeInstalledDate,
        isAdmin,
        billingPeriod,
        hadPaidPlan,
        role,
        gettingStartedSteps,
        numberOfNewNotifications,
        hasBillingManager,
        hadTrialBefore,
        trialEndsAt,
        amountOf429Renders,
        customDeal,
        onboardingSurveyCompleted,
        lastSeen,
      } = action.payload;

      return {
        ...state,
        id,
        email,
        numPagesCached,
        token,
        signupDate: new Date(signupDate), // Backend always returns this
        companyCreationDate: new Date(companyCreationDate), // Backend always returns this
        trackingCodeInstalled,
        cacheFreshness,
        hasBillingInfo,
        recachingDisabled,
        planName,
        costPerMonth,
        emailVerified,
        customPrice,
        hasCustomContractPricing,
        customCachedPagesLimit,
        recacheDelay,
        userPausedRecaching,
        queryParamsWhitelist,
        ignoreAllQueryParams,
        hasBillingAddress,
        firstName,
        lastName,
        isMeteredBilling,
        plan,
        renderCounter,
        cancelledAt: stringToDate(cancelledAt),
        mobileAdaptive,
        settings,
        features,
        allURLCount,
        companyId,
        companyName,
        adminEmail,
        isTrial,
        isCacheFreshnessDisabled,
        customRecacheDelay,
        chargebeePlanId: chargeBeePlanId,
        inProgress: false,
        hasSession: true,
        nextBillingAt: nextBillingAt ? new Date(nextBillingAt) : null,
        lastCharged: new Date(lastCharged),
        upgradedFromChargebeePlanId: action.payload.upgradedFromChargebeePlanId,
        upgradedAt: stringToDate(action.payload.upgradedAt),
        showRegistrationSteps,
        trackingCodeInstalledDate: stringToDate(trackingCodeInstalledDate),
        isAdmin,
        billingPeriod,
        hadPaidPlan,
        role,
        gettingStartedSteps,
        hasBillingManager,
        unreadNotifications:
          typeof numberOfNewNotifications === 'number' ? numberOfNewNotifications : state.unreadNotifications,
        hadTrialBefore,
        trialEndsAt: trialEndsAt ? new Date(trialEndsAt) : null,
        amountOf429Renders,
        customDeal,
        onboardingSurveyCompleted,
        lastSeen: new Date(lastSeen || signupDate),
      };
    }

    case ActionType.UserTokenSet:
      return { ...state, token: action.payload };

    case ActionType.UserLoadingSet:
      return { ...state, inProgress: action.payload.value };

    case ActionType.UserAnauthorized:
      return { ...state, hasSession: false };

    case ActionType.UserTrackingCodeSet:
      return { ...state, trackingCodeInstalled: action.payload };

    case ActionType.UserShowOnboardingSet:
      return { ...state, showOnboarding: action.payload };

    case ActionType.UserReset:
      return defaultUser;

    case ActionType.UserUnreadNotificationsDecrease:
      if (state.unreadNotifications - action.payload > -1) {
        return { ...state, unreadNotifications: state.unreadNotifications - action.payload };
      }
      return { ...state, unreadNotifications: 0 };

    case ActionType.UserUnreadNotificationsIncrease:
      return { ...state, unreadNotifications: state.unreadNotifications + action.payload };

    case ActionType.UserPatch:
      return {
        ...state,
        ...action.payload,
      };
    default:
  }
  return state;
}
